/**
 * PriorContractFormDialog Component
 * Modal for capturing a historical (pre-go-live) contract stub during the take-on phase.
 *
 * Two paths (T3-048):
 *  1. "Attach to Existing Contract" — search Historical contracts by system reference, external
 *     reference, or investor name; pick one; attach THIS investor + their capital to that exact
 *     contract by id (an explicit merge that consolidates take-on captures instead of fragmenting
 *     into a fresh stub per investor). Surfaces the system-generated reference in the results.
 *  2. Create new — the original path: type the external reference + product/currency/dates/capital
 *     and create a fresh Historical stub. Used when no existing contract matches.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Alert,
  CircularProgress,
  MenuItem,
  Autocomplete,
  Box,
  Divider,
} from '@mui/material';
import { apiPost, apiGet, extractApiData, extractApiError } from '../../utils/apiClient';
import { formatNumber } from '../../utils/formatters';
import { earliestAllowedDate } from '../../utils/backdatePolicy';

const EMPTY_FORM = {
  user_reference: '',
  product_id: '',
  deposit_date: '',
  maturity_date: '',
  investment_currency_id: '',
  capital_amount: '',
};

const PriorContractFormDialog = ({ open, onClose, onSaved, investorId, investorName }) => {
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [products, setProducts] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // T3-048 search-existing state
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedExisting, setSelectedExisting] = useState(null);
  const searchDebounce = useRef(null);

  // T3-073: non-blocking "this reference already exists" warning for the CREATE sub-form.
  // Since sp_contract_create_historical now ALWAYS creates a distinct record (no silent
  // fuzzy merge), typing an external reference that already exists would create a second
  // record. Surface any existing Historical matches so the operator can choose to attach via
  // the search above instead — advisory only, never blocks Create.
  const [dupMatches, setDupMatches] = useState([]);
  const dupDebounce = useRef(null);

  const isAttachMode = !!selectedExisting;

  // Tenant Settings → historical backdating rule (POST /contracts/historical enforces it). With
  // backdating OFF no historical stub can be created; attaching to an existing one is unaffected.
  const [historicalRule, setHistoricalRule] = useState(null);
  useEffect(() => {
    if (!open) return;
    apiGet('/contracts/settings/workflow')
      .then((res) => setHistoricalRule(res?.data?.backdate?.historical || null))
      .catch(() => setHistoricalRule(null));
  }, [open]);
  const historicalCreateDisabled = historicalRule?.enabled === false;
  const earliestHistoricalDate = earliestAllowedDate(historicalRule);

  // Load reference data + reset all state when dialog opens
  useEffect(() => {
    if (open) {
      setFormData({ ...EMPTY_FORM });
      setError(null);
      setSelectedExisting(null);
      setSearchInput('');
      setSearchOptions([]);
      setDupMatches([]);

      if (products.length === 0) {
        apiGet('/refdata/investment-types?active_only=true').then(res => {
          if (res?.success) setProducts(res.data || []);
        }).catch(() => {});
      }
      if (currencies.length === 0) {
        apiGet('/refdata/countries?active_only=true').then(res => {
          if (res?.success) setCurrencies(res.data || []);
        }).catch(() => {});
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- currencies.length and products.length are intentional one-time load guards
  }, [open]);

  // Debounced search of existing Historical contracts (system ref / external ref / investor name).
  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    const q = (searchInput || '').trim();
    if (q.length < 2) {
      setSearchOptions([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    searchDebounce.current = setTimeout(async () => {
      try {
        const res = await apiGet('/contracts/historical-search?q=' + encodeURIComponent(q));
        setSearchOptions(extractApiData(res) || []);
      } catch {
        setSearchOptions([]);
      } finally {
        setSearchLoading(false);
      }
    }, 350);
    return () => { if (searchDebounce.current) clearTimeout(searchDebounce.current); };
  }, [searchInput]);

  // T3-073: detect existing Historical records that share the typed external reference so the
  // CREATE sub-form can warn (non-blocking). Only runs in create mode. Reuses the same
  // /contracts/historical-search endpoint the "Find existing" Autocomplete uses; narrows to the
  // same product + currency when those are chosen (so the warning matches what would actually be a
  // near-duplicate). Never blocks — advisory only.
  useEffect(() => {
    if (dupDebounce.current) clearTimeout(dupDebounce.current);
    const ref = (formData.user_reference || '').trim();
    if (isAttachMode || ref.length < 2) {
      setDupMatches([]);
      return;
    }
    dupDebounce.current = setTimeout(async () => {
      try {
        const res = await apiGet('/contracts/historical-search?q=' + encodeURIComponent(ref));
        const rows = extractApiData(res) || [];
        const selProduct = products.find((p) => String(p.id) === String(formData.product_id));
        const selCurrency = currencies.find((c) => String(c.id) === String(formData.investment_currency_id));
        const matches = rows.filter((o) => {
          // exact (case-insensitive) external-reference match only — the search also hits system
          // ref + investor name, which we don't want to warn on
          if ((o.user_reference || '').trim().toLowerCase() !== ref.toLowerCase()) return false;
          if (selProduct && o.product_type && o.product_type !== selProduct.type) return false;
          if (selCurrency && o.currency_code && o.currency_code !== selCurrency.currency_code) return false;
          return true;
        });
        setDupMatches(matches);
      } catch {
        setDupMatches([]);
      }
    }, 350);
    return () => { if (dupDebounce.current) clearTimeout(dupDebounce.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- products/currencies are stable one-time loads
  }, [formData.user_reference, formData.product_id, formData.investment_currency_id, isAttachMode]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isValid = isAttachMode
    ? parseFloat(formData.capital_amount) > 0
    : (!historicalCreateDisabled
        && formData.user_reference
        && formData.product_id
        && formData.deposit_date
        && formData.maturity_date
        && formData.investment_currency_id
        && parseFloat(formData.capital_amount) > 0);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      let response;
      if (isAttachMode) {
        // Explicit id-based merge: attach this investor + capital to the chosen contract.
        response = await apiPost(`/contracts/${selectedExisting.id}/historical-attach-investor`, {
          investor_id: investorId,
          capital_amount: parseFloat(formData.capital_amount),
        });
      } else {
        response = await apiPost('/contracts/historical', {
          user_reference: formData.user_reference,
          investor_id: investorId,
          product_id: parseInt(formData.product_id),
          deposit_date: formData.deposit_date,
          maturity_date: formData.maturity_date,
          investment_currency_id: parseInt(formData.investment_currency_id),
          capital_amount: parseFloat(formData.capital_amount),
        });
      }
      if (response?.success && response.data) {
        // Carry the capital amount the user just captured back to the caller so the
        // rollover source row can be seeded with it — otherwise the user is forced to
        // re-enter the same amount in the sources panel (it rendered as 0.00). Both
        // attach and create modes capture an amount; surface it as capturedCapitalAmount.
        onSaved({ ...response.data, capturedCapitalAmount: parseFloat(formData.capital_amount) });
        onClose();
      } else {
        setError(response?.message || 'Failed to save historical contract');
      }
    } catch (err) {
      setError(extractApiError(err, 'Failed to save historical contract'));
    } finally {
      setSubmitting(false);
    }
  };

  const optionLabel = (opt) => {
    if (!opt) return '';
    const head = opt.user_reference || opt.reference || '';
    const sys = opt.user_reference && opt.reference ? ` (${opt.reference})` : '';
    return `${head}${sys}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold">Enter Historical Contract</Typography>
        <Typography variant="body2" color="textSecondary">
          Capture details of a prior contract that pre-dates the system.
        </Typography>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* T3-048: find an already-entered historical contract and attach this investor to it. */}
        <Autocomplete
          sx={{ mt: 0.5, mb: 1.5 }}
          options={searchOptions}
          value={selectedExisting}
          onChange={(_, val) => setSelectedExisting(val)}
          onInputChange={(_, val, reason) => { if (reason === 'input') setSearchInput(val); }}
          loading={searchLoading}
          getOptionLabel={optionLabel}
          isOptionEqualToValue={(o, v) => o.id === v?.id}
          filterOptions={(x) => x} // server-side search; don't re-filter locally
          noOptionsText={searchInput.trim().length < 2 ? 'Type at least 2 characters' : 'No matching historical contracts'}
          renderOption={(props, option) => {
            const { key, ...rest } = props;
            const maturity = option.maturity_date
              ? new Date(option.maturity_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
              : '';
            return (
              <li key={option.id} {...rest}>
                <Box sx={{ width: '100%', py: 0.25 }}>
                  <Typography variant="body2" fontWeight="bold">
                    {option.user_reference || option.reference}
                    {option.user_reference && option.reference && (
                      <Typography component="span" variant="body2" color="textSecondary">
                        {' '}({option.reference})
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" component="div">
                    {[option.investor_names, option.product_type,
                      `${option.currency_code || ''} ${formatNumber(option.capital_amount, 2)}`.trim()]
                      .filter(Boolean).join(' · ')}
                    {maturity ? ` · Matures ${maturity}` : ''}
                  </Typography>
                </Box>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Find an existing historical contract (optional)"
              helperText="Search by system reference, external reference, or investor to attach this investor to an existing contract"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {searchLoading ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        {isAttachMode && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Attaching <strong>{investorName || 'this investor'}</strong> to existing contract{' '}
            <strong>{selectedExisting.reference}</strong>
            {selectedExisting.user_reference ? ` (${selectedExisting.user_reference})` : ''}. Enter this
            investor&apos;s capital contribution below.
          </Alert>
        )}

        {!isAttachMode && historicalCreateDisabled && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            Historical contract backdating is turned off in Tenant Settings, so a new historical
            contract cannot be captured. You can still attach this investor to an existing one above.
          </Alert>
        )}

        <Divider sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="textSecondary">
            {isAttachMode ? 'Attaching to existing contract' : 'or capture a new prior contract'}
          </Typography>
        </Divider>

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid size={12}>
            <TextField
              label="External Reference *"
              fullWidth
              value={isAttachMode ? (selectedExisting.user_reference || '') : formData.user_reference}
              onChange={(e) => handleChange('user_reference', e.target.value)}
              helperText="The reference used for this contract in the prior system"
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly: isAttachMode }}
              variant={isAttachMode ? 'filled' : 'outlined'}
            />
          </Grid>
          {!isAttachMode && dupMatches.length > 0 && (
            <Grid size={12}>
              <Alert severity="warning" sx={{ mt: -0.5 }}>
                A historical contract with reference{' '}
                <strong>{dupMatches[0].user_reference || formData.user_reference}</strong>{' '}
                already exists
                {dupMatches.length > 1 ? ` (${dupMatches.length} matches)` : ''}:
                {' '}
                {dupMatches.slice(0, 3).map((m, i) => (
                  <span key={m.id}>
                    {i > 0 ? '; ' : ''}
                    {m.currency_code || ''} {formatNumber(m.capital_amount, 2)}
                    {m.maturity_date
                      ? `, matures ${new Date(m.maturity_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`
                      : ''}
                  </span>
                ))}
                {dupMatches.length > 3 ? '; …' : ''}.
                {' '}Creating below makes a <strong>separate</strong> record. If you meant the same
                prior contract, use <em>Find an existing historical contract</em> above to attach to it.
              </Alert>
            </Grid>
          )}
          <Grid size={12}>
            <TextField
              label="Investor"
              fullWidth
              value={investorName || ''}
              InputProps={{ readOnly: true }}
              variant="filled"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <TextField
              label="Product Type *"
              select={!isAttachMode}
              fullWidth
              value={isAttachMode ? (selectedExisting.product_type || '') : formData.product_id}
              onChange={(e) => handleChange('product_id', e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly: isAttachMode }}
              variant={isAttachMode ? 'filled' : 'outlined'}
            >
              {!isAttachMode && products.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.type}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <TextField
              label="Currency *"
              select={!isAttachMode}
              fullWidth
              value={isAttachMode ? (selectedExisting.currency_code || '') : formData.investment_currency_id}
              onChange={(e) => handleChange('investment_currency_id', e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly: isAttachMode }}
              variant={isAttachMode ? 'filled' : 'outlined'}
            >
              {!isAttachMode && currencies.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.currency_code} - {c.country}</MenuItem>
              ))}
            </TextField>
          </Grid>
          {!isAttachMode && (
            <>
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  label="Start Date *"
                  type="date"
                  fullWidth
                  value={formData.deposit_date}
                  onChange={(e) => handleChange('deposit_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={earliestHistoricalDate ? { min: earliestHistoricalDate } : undefined}
                  helperText={historicalRule?.enabled && historicalRule?.days != null
                    ? `No earlier than ${historicalRule.days} days ago (Tenant Settings)`
                    : undefined}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  label="End Date *"
                  type="date"
                  fullWidth
                  value={formData.maturity_date}
                  onChange={(e) => handleChange('maturity_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}
          <Grid size={12}>
            <TextField
              label={isAttachMode ? "This Investor's Capital Amount *" : 'Capital Amount *'}
              type="number"
              fullWidth
              value={formData.capital_amount}
              onChange={(e) => handleChange('capital_amount', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isValid || submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
        >
          {isAttachMode ? 'Attach to Existing Contract' : 'Create Historical Contract'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PriorContractFormDialog;
